import React, { useRef, useState, useEffect } from 'react';
import PdfViewer from './Pdfviewer';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSun, FiMoon, FiX, FiMenu } from 'react-icons/fi';
import { FaChevronRight, FaChevronLeft } from 'react-icons/fa';
import Swal from 'sweetalert2';
import './App.css';

const showToast = (title, icon = 'info') => {
  Swal.fire({
    toast: true,
    position: 'bottom-end',
    showConfirmButton: false,
    timer: 1500,
    timerProgressBar: true,
    icon,
    title,
  });
};

const placeholderSuggestions = [
  'L1 1-10...',
  'L1Y...',
  'Lvl 2 121-130...',
  'Level 8 51-60...',
  'L3 11-20...',
  'Level 3Word...',
  'L4W 1-10...',
  'Level 4X...',
  'levl 5 references...',
  'Lvl 5Y 1-10...',
  'All word problems',
  'Level 6 ...',
  'L7X 1-10...',
  '5',
  'level 7 word...',
  'level 1 word problems...',
  'Lvl 3Y 1-10...',
  'L6X 1-10...',
  '9',
  'Level 8 59',
  'level 3 reference',
  'ref 5',
  'All references..'
];

const levelConfigs = {
  1: { singleOnlyEnd: 200, withAnswers: ['L1_201-210'], wordProblems: ['L1W_1-10', 'L1X_1-10', 'L1Y_1-10'] },
  2: { singleOnlyEnd: 200, withAnswers: ['L2_201-210'], wordProblems: ['L2W_1-10', 'L2X_1-10', 'L2Y_1-10'] },
  3: { singleOnlyEnd: 200, withAnswers: ['L3_201-210', 'L3_211-220'], wordProblems: ['L3W_1-10', 'L3X_1-10', 'L3Y_1-10'] },
  4: { singleOnlyEnd: 200, withAnswers: ['L4_201-210'], wordProblems: ['L4W_1-10', 'L4X_1-10', 'L4Y_1-10'] },
  5: { singleOnlyEnd: 300, withAnswers: ['L5_301-310', 'L5_311-320'], wordProblems: ['L5W_1-10', 'L5X_1-10', 'L5Y_1-10'] },
  6: { singleOnlyEnd: 300, withAnswers: ['L6_301-310', 'L6_311-320', 'L6_321-330'], wordProblems: ['L6W_1-10', 'L6X_1-10'] },
  7: { singleOnlyEnd: 230, withAnswers: ['L7_231-240'], wordProblems: ['L7W_1-10', 'L7X_1-10'] },
  8: { singleOnlyEnd: 250, withAnswers: [], wordProblems: ['L8W_1-10'] },
  9: { singleOnlyEnd: 200, withAnswers: ['L9_201-210', 'L9_211-220'], wordProblems: ['L9W_1-10'] },
  10: { singleOnlyEnd: 230, withAnswers: ['L10_231-240'], wordProblems: ['L10W_1-10'] },
  11: { singleOnlyEnd: 200, withAnswers: [], wordProblems: [] },
  12: { singleOnlyEnd: 190, withAnswers: [], wordProblems: [] },
  13: { singleOnlyEnd: 230, withAnswers: [], wordProblems: [] },
};

const levels = {};

Object.entries(levelConfigs).forEach(([levelNum, { singleOnlyEnd, withAnswers, wordProblems }]) => {
  const levelKey = `Level ${levelNum}`;
  const code = `L${levelNum}`;
  const singleOnly = Array.from({ length: Math.ceil(singleOnlyEnd / 10) }, (_, i) => {
    const start = i * 10 + 1;
    const end = Math.min(start + 9, singleOnlyEnd);
    return `${code}_${start}-${end}`;
  });

  levels[levelKey] = {
    singleOnly,
    withAnswers: withAnswers || [],
    wordProblems: wordProblems || []
  };
});

levels['Level 3'].singleOnly = levels['Level 3'].singleOnly.filter(b => ![
  'L3_71-80', 'L3_81-90', 'L3_91-100', 'L3_121-130', 'L3_131-140', 'L3_141-150'
].includes(b));

levels['Level 4'].singleOnly = levels['Level 4'].singleOnly.filter(b => ![
  'L4_1-10'
].includes(b));

levels['Level 5'].singleOnly = levels['Level 5'].singleOnly.filter(b => ![
  'L5_1-10', 'L5_11-20', 'L5_21-30', 'L5_81-90', 'L5_91-100',
  'L5_121-130', 'L5_131-140', 'L5_141-150', 'L5_151-160'
].includes(b));

levels['Level 6'].singleOnly = levels['Level 6'].singleOnly.filter(b => ![
  'L6_51-60', 'L6_71-80', 'L6_121-130',
  'L6_151-160', 'L6_171-180', 'L6_191-200', 'L6_241-250', 'L6_281-290'
].includes(b));

levels['Level 7'].singleOnly = levels['Level 7'].singleOnly.filter(b => ![
  'L7_1-10', 'L7_11-20'
].includes(b));

levels['Word Problems'] = {
  L1: ['L1W_1-10', 'L1X_1-10', 'L1Y_1-10'],
  L2: ['L2W_1-10', 'L2X_1-10', 'L2Y_1-10'],
  L3: ['L3W_1-10', 'L3X_1-10', 'L3Y_1-10'],
  L4: ['L4W_1-10', 'L4X_1-10', 'L4Y_1-10'],
  L5: ['L5W_1-10', 'L5X_1-10', 'L5Y_1-10'],
  L6: ['L6W_1-10', 'L6X_1-10'],
  L7: ['L7W_1-10', 'L7X_1-10'],
  L8: ['L8W_1-10'],
  L9: ['L9W_1-10'],
  L10: ['L10W_1-10']
};

const getUserName = () => "Tutor";

function Home({ onLogout }) {
  const [animatedPlaceholder, setAnimatedPlaceholder] = useState('');
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [sidebarVisible, setSidebarVisible] = useState(window.innerWidth > 1024);
  const [selectedLevel, setSelectedLevel] = useState('Level 1');
  const [selectedBooklet, setSelectedBooklet] = useState('L1_1-10');
  const [expandedLevels, setExpandedLevels] = useState({ 'Level 1': true });
  const [darkMode, setDarkMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [pdfView, setPdfView] = useState('booklet');
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1024);
  const [touchStartX, setTouchStartX] = useState(null);
  const bookletRef = useRef();
  const answerRef = useRef();
  const isSyncingScroll = useRef(false);
  const handleTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (touchStartX === null || !answerKeySrc) return;
    const touchEndX = e.changedTouches[0].clientX;
    const deltaX = touchEndX - touchStartX;
    if (Math.abs(deltaX) > 50) {
      if (deltaX > 0 && pdfView === 'answer') {
        setIsTransitioning(hasAnswers);
        setPdfView('booklet');
      } else if (deltaX < 0 && pdfView === 'booklet') {
        setIsTransitioning(hasAnswers);
        setPdfView('answer');
      }
    }
    setTouchStartX(null);
  };

  const togglePdfView = (direction) => {
    if (!answerKeySrc) return;
    setIsTransitioning(hasAnswers);
    setPdfView((prev) => (prev === 'booklet' ? 'answer' : 'booklet'));
  };

  const toggleSidebar = () => setSidebarVisible(prev => !prev);

  
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  
  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex((prev) => (prev + 1) % placeholderSuggestions.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  
  useEffect(() => {
    const current = placeholderSuggestions[placeholderIndex];
    let charIndex = 0;
    const typeInterval = setInterval(() => {
      setAnimatedPlaceholder(current.slice(0, charIndex));
      charIndex++;
      if (charIndex > current.length) clearInterval(typeInterval);
    }, 50);
    return () => clearInterval(typeInterval);
  }, [placeholderIndex]);

  
  useEffect(() => {
    setPdfView('booklet');
    setScrollPosition(0);
    setIsTransitioning(false);
    requestAnimationFrame(() => {
      if (bookletRef.current) bookletRef.current.scrollTop = 0;
      if (answerRef.current) answerRef.current.scrollTop = 0;
    });
  }, [selectedBooklet]);
  useEffect(() => {
    if (!isDesktop && bookletRef.current && pdfView === 'booklet') {
      bookletRef.current.scrollTop = scrollPosition;
    } else if (!isDesktop && answerRef.current && pdfView === 'answer') {
      answerRef.current.scrollTop = scrollPosition;
    }
  }, [isDesktop, pdfView, scrollPosition]);
  useEffect(() => {
    if (isTransitioning) {
      const timer = setTimeout(() => {
        setIsTransitioning(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [pdfView, isTransitioning]);

  const currentLevel = levels[selectedLevel];
  const hasAnswers = Array.isArray(currentLevel?.withAnswers) && currentLevel.withAnswers.includes(selectedBooklet);
  const isWordProblems = selectedBooklet?.match(/^[L]\d+[WXY]_/);
  const isReferenceDoc = selectedBooklet?.includes('Reference doc');

  const bookletSrc = isWordProblems
    ? `/pdfs/Word_Problems/${selectedBooklet}.pdf`
    : isReferenceDoc
      ? `/pdfs/${encodeURIComponent(selectedLevel)}/${selectedBooklet}.pdf`
      : `/pdfs/${encodeURIComponent(selectedLevel)}/${selectedBooklet}.pdf`;

  const answerKeySrc = (!isWordProblems && !isReferenceDoc && hasAnswers)
    ? `/pdfs/${encodeURIComponent(selectedLevel)}/${selectedBooklet}_Ans Key.pdf`
    : null;

  
  
  
  
  
  
  
  
  
  
  
  const handleSyncScroll = (scrollTop, source) => {
    setScrollPosition(scrollTop);
    if (isSyncingScroll.current) return;
    isSyncingScroll.current = true;
    if (isDesktop) {
      if (source === 'booklet' && answerRef.current) {
        answerRef.current.scrollTop = scrollTop;
      } else if (source === 'answer' && bookletRef.current) {
        bookletRef.current.scrollTop = scrollTop;
      }
    } else if (hasAnswers && answerKeySrc) {
      if (source === 'booklet' && answerRef.current && pdfView === 'answer') {
        answerRef.current.scrollTop = scrollTop;
      } else if (source === 'answer' && bookletRef.current && pdfView === 'booklet') {
        bookletRef.current.scrollTop = scrollTop;
      }
    }
    setTimeout(() => { isSyncingScroll.current = false }, 10);
  };

  
  useEffect(() => {
    if (!isDesktop && bookletRef.current) {
      bookletRef.current.scrollTop = scrollPosition;
    }
  }, [isDesktop,scrollPosition]);

  const handleLogoutClick = () => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You will be logged out.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, logout',
      cancelButtonText: 'Cancel',
    }).then((result) => {
      if (result.isConfirmed) {
        localStorage.removeItem('isLoggedIn');
        localStorage.removeItem('userEmail');
        onLogout();
      }
    });
  };

  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode');
    if (savedDarkMode === 'true') {
      setDarkMode(true);
      document.body.classList.add('dark-mode');
    }
  }, []);

  useEffect(() => {
    const handleContextMenu = (e) => e.preventDefault();
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'p' || e.key === 's')) {
        e.preventDefault();
        alert('This action is disabled.');
      }
    };
    window.addEventListener('contextmenu', handleContextMenu);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('contextmenu', handleContextMenu);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleSearch = (input) => {
    setSearchTerm(input);
    const normalizedInput = input
      .toLowerCase()
      .replace(/[_–—]/g, ' ')
      .replace(/\s+/g, ' ')
      .replace(/\bl(?:ve?l?|l)?\b/g, 'level')
      .replace(/levl/g, 'level')
      .replace(/lvl/g, 'level')
      .replace(/\.{2,}/g, '')
      .trim();

    if (!normalizedInput) {
      setSearchResults([]);
      return;
    }

    const results = [];
    const added = new Set();
    const normalizedWords = normalizedInput.split(' ');

    const levelNumbers = normalizedWords
      .map(word => {
        const levelMatch = word.match(/^level\s*(\d{1,2})$/i);
        const lMatch = word.match(/^l(\d{1,2})$/i);
        return levelMatch ? levelMatch[1] : lMatch ? lMatch[1] : null;
      })
      .filter(Boolean);

    const reverseLevelWordMatch = normalizedInput.match(/^(\d{1,2})\s*level\s*(w(?:o(?:r(?:d(?:[^0-9\s].*)?)?)?)?)$/i);
    if (reverseLevelWordMatch) {
      const levelNum = reverseLevelWordMatch[1].replace(/^0/, '');
      const wordProblemIndicator = reverseLevelWordMatch[2];
      const levelKey = `Level ${levelNum}`;
      const content = levels[levelKey];
      if (content) {
        if (wordProblemIndicator) {
          (content.wordProblems || []).forEach((booklet) => {
            const key = `${levelKey}_${booklet}`;
            if (!added.has(key)) {
              results.push({ level: levelKey, booklet });
              added.add(key);
            }
          });
        } else {
          [...(content.singleOnly || []), ...(content.withAnswers || []), ...(content.wordProblems || [])].forEach((booklet) => {
            const key = `${levelKey}_${booklet}`;
            if (!added.has(key)) {
              results.push({ level: levelKey, booklet });
              added.add(key);
            }
          });
        }
        setSearchResults(results);
        return;
      }
    }

    const bookletsOrAnswersMatch =
      normalizedInput.match(
        /^(?:le?v?l?|l|lv|lvl|lvel|lev|leve|level)?\s*(\d{1,2})\s*(?:a|an|ans(?:\s*b(?:o(?:o(?:k(?:l(?:e(?:t(?:s)?)?)?)?)?)?)?)?|answ(?:\s*b(?:o(?:o(?:k(?:l(?:e(?:t(?:s)?)?)?)?)?)?)?)?|answe(?:\s*b(?:o(?:o(?:k(?:l(?:e(?:t(?:s)?)?)?)?)?)?)?)?|answer(?:\s*b(?:o(?:o(?:k(?:l(?:e(?:t(?:s)?)?)?)?)?)?)?)?|answers?|b|bo|boo|book|bookl|bookle|booklet|booklets)?$/i
      ) ||
      normalizedInput.match(
        /^(?:a|an|ans(?:\s*b(?:o(?:o(?:k(?:l(?:e(?:t(?:s)?)?)?)?)?)?)?)?|answ(?:\s*b(?:o(?:o(?:k(?:l(?:e(?:t(?:s)?)?)?)?)?)?)?)?|answe(?:\s*b(?:o(?:o(?:k(?:l(?:e(?:t(?:s)?)?)?)?)?)?)?)?|answer(?:\s*b(?:o(?:o(?:k(?:l(?:e(?:t(?:s)?)?)?)?)?)?)?)?|answers?|b|bo|boo|book|bookl|bookle|booklet|booklets)\s*(?:of\s*)?(?:le?v?l?|l|lv|lvl|lvel|lev|leve|level)?\s*(\d{1,2})$/i
      ) ||
      normalizedInput.match(
        /^(?:le?v?l?|l|lv|lvl|lvel|lev|leve|level)?\s*(\d{1,2})\s*((?:a|an|ans|answ|answe|answer|answers|b|bo|boo|book|bookl|bookle|booklet|booklets).*)$/i
      );

    if (bookletsOrAnswersMatch) {
      const levelNum = bookletsOrAnswersMatch[1];
      if (levelNum) {
        const levelKey = `Level ${parseInt(levelNum, 10)}`;
        const content = levels[levelKey];
        if (content) {
          const list = [
            ...(content.singleOnly || []),
            ...(content.withAnswers || []),
          ];
          list.forEach((booklet) => {
            const key = `${levelKey}_${booklet}`;
            if (!added.has(key)) {
              results.push({ level: levelKey, booklet });
              added.add(key);
            }
          });
          setSearchResults(results);
          return;
        }
      }
    }

    const allMatch = normalizedInput.match(
      /^(?:a|al|all)\s*(b|bo|boo|book|bookl|bookle|booklet|booklets|a|an|ans|answ|answer|answers)$/i
    );

    if (allMatch) {
      const type = allMatch[1];
      Object.entries(levels).forEach(([levelKey, content]) => {
        if (levelKey === "Word Problems") return;
        let list = [];
        if (/^(b|bo|boo|book|bookl|bookle|booklet|booklets)$/i.test(type)) {
          list = [...(content.singleOnly || []), ...(content.withAnswers || [])];
        } else if (/^(a|an|ans|answ|answer|answers)$/i.test(type)) {
          list = content.withAnswers || [];
        }
        list.forEach((booklet) => {
          const key = `${levelKey}_${booklet}`;
          if (!added.has(key)) {
            results.push({ level: levelKey, booklet });
            added.add(key);
          }
        });
      });
      setSearchResults(results);
      return;
    }

    const exactLevelMatch = normalizedInput.match(/^l(\d{1,2})$/i);
    if (exactLevelMatch) {
      const levelNum = parseInt(exactLevelMatch[1], 10);
      if (levelNum > 13) {
        setSearchResults([]);
        return;
      }
      const levelKey = `Level ${levelNum}`;
      const content = levels[levelKey];
      if (content) {
        [...(content.singleOnly || []), ...(content.withAnswers || []), ...(content.wordProblems || [])].forEach((booklet) => {
          const key = `${levelKey}_${booklet}`;
          if (!added.has(key)) {
            results.push({ level: levelKey, booklet });
            added.add(key);
          }
        });
        setSearchResults(results);
        return;
      }
    }

    const fullLevelMatch = normalizedInput.match(/^level\s*(\d{1,2})$/i);
    if (fullLevelMatch) {
      const levelNum = parseInt(fullLevelMatch[1], 10);
      if (levelNum > 13) {
        setSearchResults([]);
        return;
      }
      const levelKey = `Level ${levelNum}`;
      const content = levels[levelKey];
      if (content) {
        [...(content.singleOnly || []), ...(content.withAnswers || []), ...(content.wordProblems || [])].forEach((booklet) => {
          const key = `${levelKey}_${booklet}`;
          if (!added.has(key)) {
            results.push({ level: levelKey, booklet });
            added.add(key);
          }
        });
        setSearchResults(results);
        return;
      }
    }

    const numberFirstLevelMatch = normalizedInput.match(/^(\d{1,2})\s*(?:levl|lvl?|level|lvel|l|le|lev|leve)$/i);
    if (numberFirstLevelMatch) {
      const levelNum = parseInt(numberFirstLevelMatch[1], 10);
      if (levelNum > 13) {
        setSearchResults([]);
        return;
      }
      const levelKey = `Level ${levelNum}`;
      const content = levels[levelKey];
      if (content) {
        [...(content.singleOnly || []), ...(content.withAnswers || []), ...(content.wordProblems || [])].forEach((booklet) => {
          const key = `${levelKey}_${booklet}`;
          if (!added.has(key)) {
            results.push({ level: levelKey, booklet });
            added.add(key);
          }
        });
        setSearchResults(results);
        return;
      }
    }

    const levelRegex = /^(?:levl|lvl?|level|lvel|l|le|lev|leve)\s*(\d{1,2})\s+(\d{1,3})\s*[-–]\s*(\d{1,3})$/i;
    const match = normalizedInput.match(levelRegex);
    if (match) {
      const [, levelNumRaw, start, end] = match;
      const levelNum = parseInt(levelNumRaw, 10);
      if (levelNum > 13) {
        setSearchResults([]);
        return;
      }
      const levelName = `Level ${levelNum}`;
      const bookletCode = `L${levelNum}_${start}-${end}`;
      const content = levels[levelName];
      if (content) {
        const all = [...(content.singleOnly || []), ...(content.withAnswers || [])];
        if (all.includes(bookletCode)) {
          const key = `${levelName}_${bookletCode}`;
          if (!added.has(key)) {
            results.push({ level: levelName, booklet: bookletCode });
            added.add(key);
          }
          setSearchResults(results);
          return;
        }
      }
    }

    const compactMatch = normalizedInput.match(/^l(\d{1,2})(\d+)$/i);
    if (compactMatch) {
      const levelNum = parseInt(compactMatch[1], 10);
      const bookletNum = compactMatch[2];
      if (levelNum > 13) {
        setSearchResults([]);
        return;
      }
      const levelKey = `Level ${levelNum}`;
      const content = levels[levelKey];
      if (content) {
        const bookletCode = `L${levelNum}_${bookletNum}`;
        const all = [...(content.singleOnly || []), ...(content.withAnswers || []), ...(content.wordProblems || [])];
        if (all.includes(bookletCode)) {
          const key = `${levelKey}_${bookletCode}`;
          if (!added.has(key)) {
            results.push({ level: levelKey, booklet: bookletCode });
            added.add(key);
          }
          setSearchResults(results);
          return;
        }
      }
    }

    const wpMatch = normalizedInput.match(/^(?:levl|lvl?|level|lvel|l|le|lev|leve)?\s*(\d{1,2})?\s*(w(?:o(?:r(?:d(?:\s*p(?:r(?:o(?:b(?:l(?:e(?:m(?:s*?)?)?)?)?)?)?)?)?)?)?)?)$/i);
    if (wpMatch) {
      const levelNumRaw = wpMatch[1];
      const levelNum = levelNumRaw ? levelNumRaw.replace(/^0/, '') : null;
      if (levelNum) {
        const levelKey = `Level ${levelNum}`;
        const content = levels[levelKey];
        if (content && content.wordProblems) {
          content.wordProblems.forEach((booklet) => {
            const key = `${levelKey}_${booklet}`;
            if (!added.has(key)) {
              results.push({ level: levelKey, booklet });
              added.add(key);
            }
          });
          setSearchResults(results);
          return;
        }
      } else {
        Object.entries(levels).forEach(([levelKey, content]) => {
          if (levelKey !== "Word Problems" && content.wordProblems) {
            content.wordProblems.forEach((booklet) => {
              const key = `${levelKey}_${booklet}`;
              if (!added.has(key)) {
                results.push({ level: levelKey, booklet });
                added.add(key);
              }
            });
          }
        });
        setSearchResults(results);
        return;
      }
    }

    const reverseWpMatch = normalizedInput.match(
      /^(w(?:o(?:r(?:d(?:\s*p(?:r(?:o(?:b(?:l(?:e(?:m(?:s*?)?)?)?)?)?)?)?)?)?)?)?)\s*(?:levl|lvl?|level|lvel|l|le|lev|leve)?\s*(\d{1,2})$/i
    );
    if (reverseWpMatch) {
      const levelNumRaw = reverseWpMatch[2];
      const levelNum = levelNumRaw ? levelNumRaw.replace(/^0/, '') : null;
      if (levelNum) {
        const levelKey = `Level ${levelNum}`;
        const content = levels[levelKey];
        if (content && content.wordProblems) {
          content.wordProblems.forEach((booklet) => {
            const key = `${levelKey}_${booklet}`;
            if (!added.has(key)) {
              results.push({ level: levelKey, booklet });
              added.add(key);
            }
          });
          setSearchResults(results);
          return;
        }
      }
    }

    const refOnlyMatch = normalizedInput.match(
      /^(r|re|ref|refe|refer|refere|referen|referenc|reference)(\s*(d|do|doc|docu|docum|docume|documen|document|docs|documents))?$/i
    );
    if (refOnlyMatch) {
      Object.keys(levels).forEach((levelKey) => {
        const num = parseInt(levelKey.match(/\d+/)?.[0], 10);
        if (num && num >= 3 && num <= 12) {
          const refBooklet = `Level ${num} Reference doc`;
          results.push({ level: levelKey, booklet: refBooklet });
        }
      });
      setSearchResults(results);
      return;
    }

    const allWordProblemsMatch = normalizedInput.match(/^(?:a|al|all)\s*(w(?:o(?:r(?:d(?:\s*p(?:r(?:o(?:b(?:l(?:e(?:m(?:s*?)?)?)?)?)?)?)?)?)?)?)?)$/i);
    if (allWordProblemsMatch) {
      Object.entries(levels).forEach(([levelKey, content]) => {
        if (levelKey !== "Word Problems" && content.wordProblems) {
          content.wordProblems.forEach((booklet) => {
            const key = `${levelKey}_${booklet}`;
            if (!added.has(key)) {
              results.push({ level: levelKey, booklet });
              added.add(key);
            }
          });
        }
      });
      setSearchResults(results);
      return;
    }

    const allRefMatch = normalizedInput.match(/^(?:a|al|all)\s*(r|re|ref|refer|reference)$/i);
    if (allRefMatch) {
      Object.keys(levels).forEach((levelKey) => {
        const num = parseInt(levelKey.match(/\d+/)?.[0], 10);
        if (num && num >= 3 && num <= 12) {
          const refBooklet = `Level ${num} Reference doc`;
          const key = `${levelKey}_${refBooklet}`;
          if (!added.has(key)) {
            results.push({ level: levelKey, booklet: refBooklet });
            added.add(key);
          }
        }
      });
      setSearchResults(results);
      return;
    }

    const numberFirstMatch = normalizedInput.match(
      /^(\d{1,2})\s*(?:lvl?|level|le+|lv+|)\s*(word(?:\s*(?:problems+?|prob|p|pr|pro|probl|proble|problem))?)$/i
    );
    if (numberFirstMatch) {
      const levelNum = numberFirstMatch[1].replace(/^0/, '');
      const levelKey = `Level ${levelNum}`;
      const content = levels[levelKey];
      if (content && content.wordProblems) {
        content.wordProblems.forEach((booklet) => {
          const key = `${levelKey}_${booklet}`;
          if (!added.has(key)) {
            results.push({ level: levelKey, booklet });
            added.add(key);
          }
        });
        setSearchResults(results);
        return;
      }
    }

    const levelNumberMatch = normalizedInput.match(/(?:level|l)\s*(\d{1,2})\s*(\d{1,3})/i);
    if (levelNumberMatch) {
      const levelNum = parseInt(levelNumberMatch[1], 10);
      const number = parseInt(levelNumberMatch[2], 10);
      const levelKey = `Level ${levelNum}`;
      const content = levels[levelKey];
      if (content) {
        const start = Math.floor((number - 1) / 10) * 10 + 1;
        const end = start + 9;
        const bookletCode = `L${levelNum}_${start}-${end}`;
        const all = [...(content.singleOnly || []), ...(content.withAnswers || [])];
        if (all.includes(bookletCode)) {
          const key = `${levelKey}_${bookletCode}`;
          if (!added.has(key)) {
            results.push({ level: levelKey, booklet: bookletCode });
            added.add(key);
            setSearchResults(results);
            return;
          }
        } else {
          [...(content.singleOnly || []), ...(content.withAnswers || []), ...(content.wordProblems || [])].forEach((booklet) => {
            const key = `${levelKey}_${booklet}`;
            if (!added.has(key)) {
              results.push({ level: levelKey, booklet });
              added.add(key);
            }
          });
          setSearchResults([]);
          return;
        }
      }
    }

    const refMatch = normalizedInput.match(
      /(\d{1,2}).*?(?:r(?:e(?:f(?:e(?:r(?:e(?:n(?:c(?:e)?)?)?)?)?)?)?)?)|(?:r(?:e(?:f(?:e(?:r(?:e(?:n(?:c(?:e)?)?)?)?)?)?)?)?).*?(\d{1,2})/i
    );
    if (refMatch) {
      const levelNumRaw = refMatch[1] || refMatch[2];
      const levelNum = levelNumRaw ? levelNumRaw.replace(/^0/, '') : null;
      if (levelNum) {
        const levelKey = `Level ${levelNum}`;
        const refBooklet = `Level ${levelNum} Reference doc`;
        if (levels[levelKey]) {
          results.push({ level: levelKey, booklet: refBooklet });
          setSearchResults(results);
          return;
        }
      } else {
        Object.keys(levels).forEach((levelKey) => {
          const num = parseInt(levelKey.match(/\d+/)?.[0], 10);
          if (num && num >= 3 && num <= 12) {
            const refBooklet = `Level ${num} Reference doc`;
            results.push({ level: levelKey, booklet: refBooklet });
          }
        });
        setSearchResults(results);
        return;
      }
    }

    const numberOnlyMatch = normalizedInput.match(/^(\d{1,2})$/);
    if (numberOnlyMatch) {
      const levelNum = numberOnlyMatch[1].replace(/^0/, '');
      const levelKey = `Level ${levelNum}`;
      const content = levels[levelKey];
      if (content) {
        [...(content.singleOnly || []), ...(content.withAnswers || []), ...(content.wordProblems || [])]
          .forEach((booklet) => {
            const key = `${levelKey}_${booklet}`;
            if (!added.has(key)) {
              results.push({ level: levelKey, booklet });
              added.add(key);
            }
          });
        setSearchResults(results);
        return;
      }
    }

    const refShortcutMatch = normalizedInput.match(
      /^(?:le?v?e?l?|l)\s*(\d{1,2})\s*(r|re|ref|refer|reference)$|^(r|re|ref|refer|reference)\s*(?:le?v?e?l?|l)?\s*(\d{1,2})$/i
    );
    if (refShortcutMatch) {
      const levelNum = refShortcutMatch[1] || refShortcutMatch[4];
      if (levelNum) {
        const levelKey = `Level ${parseInt(levelNum, 10)}`;
        const refBooklet = `${levelKey} Reference doc`;
        if (levels[levelKey]) {
          results.push({ level: levelKey, booklet: refBooklet });
          setSearchResults(results);
          return;
        }
      }
    }

    const levelShortcutMatch = normalizedInput.match(
      /^(?:le?v?e?l?|l|le|lev|leve|lvel)\s*(\d{1,2})$/i
    );
    if (levelShortcutMatch) {
      const levelNum = levelShortcutMatch[1];
      if (levelNum) {
        const levelKey = `Level ${parseInt(levelNum, 10)}`;
        const content = levels[levelKey];
        if (content) {
          [...(content.singleOnly || []), ...(content.withAnswers || []), ...(content.wordProblems || [])]
            .forEach((booklet) => {
              const key = `${levelKey}_${booklet}`;
              if (!added.has(key)) {
                results.push({ level: levelKey, booklet });
                added.add(key);
              }
            });
          setSearchResults(results);
          return;
        }
      }
    }

    const partialMatch = (target) => {
      const normalizedTarget = target.trim().toLowerCase().replace(/[_–—]/g, ' ');
      return normalizedWords.every((word) => normalizedTarget.includes(word));
    };

    Object.entries(levels).forEach(([levelName, content]) => {
      if (levelName === 'Word Problems') return;
      const levelNum = levelName.match(/\d+/)?.[0];
      if (levelNumbers.length > 0 && (!levelNum || !levelNumbers.includes(levelNum))) {
        return;
      }
      const all = [
        ...(content.singleOnly || []),
        ...(content.withAnswers || []),
        ...(content.wordProblems || []),
      ];
      all.forEach((b) => {
        const display = `${levelName} ${b}`;
        const key = `${levelName}_${b}`;
        if (partialMatch(display) && !added.has(key)) {
          results.push({ level: levelName, booklet: b });
          added.add(key);
        }
      });
    });

    setSearchResults(results);
  };

  const toggleDarkMode = () => {
    setDarkMode((prev) => {
      const newMode = !prev;
      localStorage.setItem('darkMode', newMode);
      if (newMode) document.body.classList.add('dark-mode');
      else document.body.classList.remove('dark-mode');
      return newMode;
    });
  };

  const userEmail = localStorage.getItem('userEmail');
  const userName = getUserName(userEmail);

  return (
    <div className={`app-container ${darkMode ? 'dark' : ''}`}>
      <div className={`sidebar ${sidebarVisible ? 'visible' : 'hidden'}`}>
        <div className="welcome-card">👋 Hello {userName}!</div>
        <div className="level-scroll-area">
          {Object.entries(levels).map(([level, booklets]) => {
            const isExpanded = expandedLevels[level];
            const levelNum = parseInt(level.match(/\d+/)?.[0]);
            const isLevel1or2 = levelNum === 1 || levelNum === 2;
            const hasRefDoc = levelNum >= 3 && levelNum <= 12;

            const allStandard = booklets.singleOnly || [];
            const recentWithAnswers = booklets.withAnswers || [];
            const wordProblems = booklets.wordProblems || [];

            return (
              <div key={level}>
                <button
                  className={`level-btn ${level === 'Word Problems' && selectedLevel.startsWith('Level') ? 'active' : selectedLevel === level ? 'active' : ''}`}
                  onClick={() => {
                    const first = allStandard[0] || recentWithAnswers[0] || wordProblems[0];
                    const newState = !isExpanded;
                    setSelectedLevel(level);
                    setSelectedBooklet(first);
                    setExpandedLevels({ [level]: newState });
                    showToast(`${level} ${newState ? 'expanded' : 'collapsed'}`, 'info');
                  }}
                >
                  {level}
                  <span className={`arrow ${isExpanded ? 'rotate' : ''}`}>
                    <FaChevronRight />
                  </span>
                </button>

                <AnimatePresence initial={false}>
                  {isExpanded && (
                    <motion.div
                      className="booklet-list"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      {hasRefDoc && (
                        <>
                          <div className="word-problem-label">
                            {levelNum === 12 ? 'Word Problems' : 'Reference Docs'}
                          </div>
                          <button
                            key={`ref-${levelNum}`}
                            className={`booklet-btn ${selectedBooklet === `Level ${levelNum} Reference doc` ? 'selected' : ''}`}
                            onClick={() => {
                              setSelectedLevel(level);
                              setSelectedBooklet(`Level ${levelNum} Reference doc`);
                            }}
                          >
                            {`Level ${levelNum} ${levelNum === 12 ? 'Word Problems' : 'Reference doc'}`}
                          </button>
                        </>
                      )}
                      {level !== 'Word Problems' && (
                        <div className="word-problem-label">
                          {isLevel1or2 ? 'Q&A Booklets' : 'Answer Key'}
                        </div>
                      )}
                      {allStandard.map((booklet) => (
                        <button
                          key={booklet}
                          className={`booklet-btn ${selectedBooklet === booklet ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedLevel(level);
                            setSelectedBooklet(booklet);
                          }}
                        >
                          {booklet.replace(/_/g, ' ')}
                        </button>
                      ))}
                      {recentWithAnswers.length > 0 && (
                        <>
                          <div className="word-problem-label">Recently Added Booklets</div>
                          {recentWithAnswers.map((booklet) => (
                            <button
                              key={booklet}
                              className={`booklet-btn ${selectedBooklet === booklet ? 'selected' : ''}`}
                              onClick={() => {
                                setSelectedLevel(level);
                                setSelectedBooklet(booklet);
                              }}
                            >
                              {booklet.replace(/_/g, ' ')}
                            </button>
                          ))}
                        </>
                      )}
                      {wordProblems.length > 0 && (
                        <>
                          <div className="word-problem-label">Word Problems</div>
                          {wordProblems.map((booklet) => (
                            <button
                              key={booklet}
                              className={`booklet-btn ${selectedBooklet === booklet ? 'selected' : ''}`}
                              onClick={() => {
                                setSelectedLevel(level);
                                setSelectedBooklet(booklet);
                              }}
                            >
                              {booklet.replace(/_/g, ' ')}
                            </button>
                          ))}
                        </>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
          <div className="word-problem-group">
            <AnimatePresence initial={false}>
              {expandedLevels['Word Problems'] && (
                <motion.div
                  className="booklet-list"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {Object.entries(levels['Word Problems']).map(([levelKey, booklets]) => {
                    const label = `Level ${levelKey.replace('L', '')}`;
                    const isExpanded = expandedLevels[label];
                    return (
                      <div key={label}>
                        <button
                          className={`wp-level-btn ${selectedLevel === label ? 'active' : ''}`}
                          onClick={() => {
                            const first = booklets[0];
                            const expanded = !expandedLevels[label];
                            setSelectedLevel(label);
                            setSelectedBooklet(first);
                            setExpandedLevels(prev => {
                              const newState = Object.keys(prev).reduce((acc, key) => {
                                if (key.startsWith('Level') && key !== label) {
                                  acc[key] = false;
                                } else if (key === 'Word Problems') {
                                  acc[key] = prev[key];
                                }
                                return acc;
                              }, {});
                              newState['Word Problems'] = true;
                              showToast(`${label} ${expanded ? 'expanded' : 'collapsed'}`, 'info');
                              return { ...newState, [label]: expanded };
                            });
                          }}
                        >
                          {label}
                          <span className={`arrow ${isExpanded ? 'rotate' : ''}`}>
                            <FaChevronRight />
                          </span>
                        </button>
                        <AnimatePresence initial={false}>
                          {isExpanded && (
                            <motion.div
                              className="booklet-list"
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              {booklets.map((booklet) => (
                                <button
                                  key={booklet}
                                  className={`booklet-btn ${selectedBooklet === booklet ? 'selected' : ''}`}
                                  onClick={() => {
                                    setSelectedLevel(label);
                                    setSelectedBooklet(booklet);
                                  }}
                                >
                                  {booklet.replace(/_/g, ' ')}
                                </button>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
        <button className="logout-btn" onClick={handleLogoutClick}>Logout</button>
      </div>
      <AnimatePresence>
        {sidebarVisible && !isDesktop && (
          <motion.div
            className="sidebar-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setSidebarVisible(false)}
          />
        )}
      </AnimatePresence>
      <div className="right-container">
        <div className="top-bar">
          <button className="burger-btn" onClick={toggleSidebar}>
            <FiMenu size={24} />
          </button>
          <div className="search-bar">
            <div className="search-container">
              <input
                type="text"
                className="search-input"
                placeholder=""
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
              />
              {searchTerm === '' && (
                <div className="placeholder-wrapper">
                  <span className="fixed-text">Search</span>
                  <span key={placeholderIndex} className="animated-text">
                    {animatedPlaceholder}
                  </span>
                </div>
              )}
              {searchTerm !== '' && (
                <button className="clear-button" onClick={() => handleSearch('')}>
                  <FiX size={20} />
                </button>
              )}
            </div>
          </div>
          <div className="button-box">
            <label className="custom-toggle">
              <input type="checkbox" checked={darkMode} onChange={toggleDarkMode} />
              <span className="slider">
                <FiSun className="icon sun" />
                <FiMoon className="icon moon" />
              </span>
            </label>
          </div>
        </div>
        {searchResults.length > 0 && (
          <div className="search-results">
            {searchResults.map((res, idx) => (
              <div
                key={idx}
                className="search-result-item"
                onClick={() => {
                  setSelectedLevel(res.level);
                  setSelectedBooklet(res.booklet);
                  setExpandedLevels({ [res.level]: true });
                  setSearchResults([]);
                  setSearchTerm('');
                }}
              >
                <span>{res.level}</span>
                {res.wordLevel && (
                  <>
                    <FaChevronRight style={{ margin: '0 6px', fontSize: '12px' }} />
                    <span>{res.wordLevel.replace(/_/g, ' ')}</span>
                  </>
                )}
                <FaChevronRight style={{ margin: '0 6px', fontSize: '12px' }} />
                <span>{res.booklet.replace(/_/g, ' ')}</span>
              </div>
            ))}
          </div>
        )}
        <div
          className="viewer-container"
          onTouchStart={isDesktop ? null : handleTouchStart}
          onTouchEnd={isDesktop ? null : handleTouchEnd}
        >
          {isDesktop ? (
            <>
              <div className={`pdf-box ${isTransitioning ? 'transitioning' : ''}`}>
                <PdfViewer
                  src={bookletSrc}
                  label={
                    selectedBooklet?.includes('Reference doc') && selectedLevel === 'Level 12'
                      ? 'Level 12 Word Problems'
                      : selectedBooklet
                        ? selectedBooklet.replace(/_/g, ' ')
                        : ''
                  }
                  colorClass="blue-box"
                  onScroll={(scrollTop) => handleSyncScroll(scrollTop, 'booklet')}
                  scrollContainerRef={bookletRef}
                  showSearch={!isReferenceDoc}
                />
              </div>
              {answerKeySrc && (
                <div className={`pdf-box ${isTransitioning ? 'transitioning' : ''}`}>
                  <PdfViewer
                    src={answerKeySrc}
                    label="Answer Key"
                    colorClass="purple-box"
                    onScroll={(scrollTop) => handleSyncScroll(scrollTop, 'answer')}
                    scrollContainerRef={answerRef}
                    showSearch={false}
                  />
                </div>
              )}
            </>
          ) : (
            <>
              <div className={`pdf-box ${isTransitioning ? 'transitioning' : ''}`}>
                {pdfView === 'booklet' ? (
                  <PdfViewer
                    src={bookletSrc}
                    label={
                      selectedBooklet?.includes('Reference doc') && selectedLevel === 'Level 12'
                        ? 'Level 12 Word Problems'
                        : selectedBooklet
                          ? selectedBooklet.replace(/_/g, ' ')
                          : ''
                    }
                    colorClass="blue-box"
                    onScroll={(scrollTop) => handleSyncScroll(scrollTop, 'booklet')}
                    scrollContainerRef={bookletRef}
                    showSearch={!isReferenceDoc}
                  />
                ) : (
                  <PdfViewer
                    src={answerKeySrc}
                    label="Answer Key"
                    colorClass="purple-box"
                    onScroll={(scrollTop) => handleSyncScroll(scrollTop, 'answer')}
                    scrollContainerRef={answerRef}
                    showSearch={false}
                  />
                )}
              </div>
              {answerKeySrc && hasAnswers && (
                <>
                  <button
                    className="pdf-toggle-btn left"
                    onClick={() => togglePdfView('left')}
                    disabled={pdfView === 'booklet'}
                  >
                    <FaChevronLeft />
                  </button>
                  <button
                    className="pdf-toggle-btn right"
                    onClick={() => togglePdfView('right')}
                    disabled={pdfView === 'answer'}
                  >
                    <FaChevronRight />
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Home;